/**
 * Source Modeling - Progressive Disclosure Model
 *
 * Sources are defined with minimal config for simple cases,
 * with complexity added only when needed.
 */

// ============================================
// Core Source Type
// ============================================

export type Source = {
  // Required
  title: string;
  description?: string;

  // Content acquisition (pick one pattern)
  url?: string; // Direct fetch - simplest
  site?: string; // Discovery base URL
  scope?: string; // Path/pattern to crawl (used with site)

  // Optional modifiers (applied in order)
  filter?: string; // LLM filter criteria
  select?: SelectStrategy; // Selection strategy (default: all)

  // Required - what to extract
  extract: string | ExtractFields; // Freeform or structured

  // Full control (overrides simple config)
  steps?: Step[];

  // Runtime inputs
  inputs?: SourceInput[];
};

export type SelectStrategy = 'random' | 'first' | 'best' | SelectConfig;

export type SelectConfig = {
  strategy: 'random' | 'first' | 'best' | 'weighted';
  count?: number;
  weightBy?: string;
};

export type ExtractFields = Record<string, string>;

export type SourceInput = {
  name: string;
  type: 'string' | 'url' | 'text' | 'number';
  required?: boolean;
  default?: string;
  description?: string;
};

// ============================================
// Step Types (for full control mode)
// ============================================

export type Step =
  | { type: 'discover'; config: DiscoverConfig }
  | { type: 'fetch'; config: string }
  | { type: 'filter'; config: string }
  | { type: 'select'; config: SelectConfig }
  | { type: 'extract'; config: ExtractFields }
  | { type: 'transform'; config: string };

export type DiscoverConfig = {
  site: string;
  scope?: string;
  depth?: number;
};

// ============================================
// Complexity Levels
// ============================================

export type SourceComplexity = 'fetch' | 'discover' | 'filter' | 'select' | 'custom';

export function inferComplexity(source: Source): SourceComplexity {
  if (source.steps && source.steps.length > 0) return 'custom';
  if (source.select) return 'select';
  if (source.filter) return 'filter';
  if (source.site) return 'discover';
  return 'fetch';
}

export function inferWorkflow(source: Source): string[] {
  if (source.steps && source.steps.length > 0) {
    return source.steps.map((s) => s.type);
  }

  const workflow: string[] = [];

  if (source.url) {
    workflow.push('fetch');
  } else if (source.site) {
    workflow.push('discover');
  }

  if (source.filter) {
    workflow.push('filter');
  }

  if (source.select) {
    workflow.push('select');
    // When selecting, we need to fetch the selected item's details
    if (source.site) {
      workflow.push('fetch');
    }
  }

  workflow.push('extract');

  return workflow;
}

// ============================================
// Defaults
// ============================================

export const defaultSource: Source = {
  title: '',
  extract: '',
};

// ============================================
// Example Scenarios
// ============================================

export type SourceScenario = {
  id: string;
  name: string;
  description: string;
  complexity: SourceComplexity;
  source: Source;
};

// ============================================
// Legacy Types (for backwards compatibility)
// ============================================

// Simple Model (Legacy)
export type SimpleSource = {
  title: string;
  prompt: string;
  capabilities: SimpleCapability[];
  capabilityConfig: SimpleCapabilityConfig;
};

export type SimpleCapability = 'web_fetch' | 'web_discover';

export type SimpleCapabilityConfig = {
  web_fetch?: { url: string };
  web_discover?: { baseUrl: string; hint?: string };
};

export const defaultSimpleSource: SimpleSource = {
  title: '',
  prompt: '',
  capabilities: [],
  capabilityConfig: {},
};

// Flexible Model (Legacy)
export type FlexibleSource = {
  title: string;
  prompt: string;
  inputs: FlexibleInput[];
  tools: FlexibleTool[];
};

export type FlexibleInput = {
  name: string;
  type: 'string' | 'url' | 'text';
  required: boolean;
  description?: string;
};

export type FlexibleTool = {
  type: 'web_fetch' | 'web_discover';
  config: Record<string, string>;
};

export const defaultFlexibleSource: FlexibleSource = {
  title: '',
  prompt: '',
  inputs: [],
  tools: [],
};

// Pipeline Model (Legacy)
export type PipelineSource = {
  title: string;
  inputs: PipelineInput[];
  steps: LegacyPipelineStep[];
};

export type PipelineInput = {
  name: string;
  type: 'string' | 'url' | 'text';
  required: boolean;
};

export type LegacyPipelineStep =
  | { type: 'web_fetch'; url: string }
  | { type: 'web_discover'; baseUrl: string; hint?: string }
  | { type: 'llm'; prompt: string };

// Alias for backwards compatibility
export type PipelineStep = LegacyPipelineStep;

export const defaultPipelineSource: PipelineSource = {
  title: '',
  inputs: [],
  steps: [],
};

// Legacy Scenarios
export type LegacySourceScenario = {
  id: string;
  name: string;
  description: string;
};

export const LEGACY_SCENARIOS: LegacySourceScenario[] = [
  { id: 'pure-llm', name: 'Pure LLM', description: 'Generate content without external data' },
  { id: 'url-summary', name: 'URL Summary', description: 'Summarize content from a specific URL' },
  {
    id: 'site-discovery',
    name: 'Site Discovery',
    description: 'Find and extract from pages within a site',
  },
  {
    id: 'complex-extraction',
    name: 'Complex Extraction',
    description: 'Extract structured data with detailed instructions',
  },
];

// ============================================
// New Progressive Model Scenarios
// ============================================

export const SCENARIOS: SourceScenario[] = [
  {
    id: 'simple-article',
    name: 'Article Summary',
    description: 'Fetch and extract from a single URL',
    complexity: 'fetch',
    source: {
      title: 'Article Summary',
      url: 'https://techcrunch.com/2024/01/15/sample-article',
      extract: 'main points, key quotes, and author opinion',
    },
  },
  {
    id: 'product-list',
    name: 'Product Catalog',
    description: 'Discover and extract from multiple pages',
    complexity: 'discover',
    source: {
      title: 'Product Catalog',
      site: 'iherb.com',
      scope: '/c/supplements',
      extract: {
        product_name: 'product title',
        price: 'current price',
        rating: 'star rating',
      },
    },
  },
  {
    id: 'filtered-products',
    name: 'Top Rated Products',
    description: 'Discover, filter by criteria, extract',
    complexity: 'filter',
    source: {
      title: 'Top Rated Supplements',
      site: 'iherb.com',
      scope: '/c/supplements',
      filter: '4+ star rating with at least 100 reviews',
      extract: {
        product_name: 'product title',
        price: 'current price',
        rating: 'star rating',
        review_count: 'number of reviews',
      },
    },
  },
  {
    id: 'random-product',
    name: 'Random Good Product',
    description: 'Discover, filter, select one randomly',
    complexity: 'select',
    source: {
      title: 'iHerb Supplement Finder',
      description: 'Finds a random health supplement with positive reviews',
      site: 'iherb.com',
      scope: '/c/supplements',
      filter: 'positive reviews, clear health benefits',
      select: 'random',
      extract: {
        product_name: 'product title',
        product_url: 'full URL',
        price: 'current price',
        benefits: 'health benefits mentioned (list)',
        review_quotes: '3-5 positive review excerpts',
      },
    },
  },
  {
    id: 'custom-workflow',
    name: 'Custom Workflow',
    description: 'Full control with explicit steps',
    complexity: 'custom',
    source: {
      title: 'Advanced Product Research',
      inputs: [
        {
          name: 'category',
          type: 'string',
          required: true,
          description: 'Product category to search',
        },
      ],
      steps: [
        {
          type: 'discover',
          config: { site: 'iherb.com', scope: '/c/{{inputs.category}}', depth: 2 },
        },
        { type: 'filter', config: '4+ stars AND verified purchase reviews' },
        { type: 'select', config: { strategy: 'weighted', weightBy: 'review_count', count: 1 } },
        { type: 'fetch', config: '{{selected.url}}' },
        {
          type: 'extract',
          config: {
            product_name: 'exact product title',
            benefits: 'list of health benefits from description',
            review_quotes: '3 most helpful positive reviews',
          },
        },
      ],
      extract: '', // Handled by steps
    },
  },
];
